import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Upload, Camera, FileText, X, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useCompany } from '@/context/CompanyContext';
import { invoiceIntakeService } from '@/lib/invoiceIntakeService';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,application/pdf';
const SCAN_MESSAGES = ['מעלה קובץ...', 'סורק עם AI...', 'מתאם פריטים...'];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function InvoiceUploadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeCompany } = useCompany();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(SCAN_MESSAGES[0]);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    if (!isScanning) return;
    let idx = 0;
    const interval = setInterval(() => {
      idx = (idx + 1) % SCAN_MESSAGES.length;
      setScanProgress(SCAN_MESSAGES[idx]);
    }, 2500);
    return () => clearInterval(interval);
  }, [isScanning]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const handleFile = useCallback((file: File) => {
    setError(null);
    setSelectedFile(file);
    setPreviewUrl(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const clearSelection = () => { setSelectedFile(null); setPreviewUrl(null); setError(null); };

  const handleScan = async () => {
    if (!selectedFile || !activeCompany?.id) return;
    setIsScanning(true);
    setScanProgress(SCAN_MESSAGES[0]);
    setError(null);
    try {
      const result = await invoiceIntakeService.uploadAndScan(activeCompany.id, selectedFile);
      if (result.error || !result.data) throw new Error(typeof result.error === 'object' && result.error && 'message' in result.error ? (result.error as any).message : 'Scan failed');
      navigate(`/app/invoice-intake/review/${result.data.invoice_id}`);
    } catch (err: any) {
      setError(err?.message || t('invoiceIntake.upload.error', 'שגיאה בסריקת החשבונית. נסה שוב.'));
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/app/invoice-intake')}
          className="p-2 rounded-lg hover:bg-teal-100 text-teal-700 transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">
          {t('invoiceIntake.upload.title', 'העלאת חשבונית ספק')}
        </h1>
      </div>

      {!selectedFile ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            relative flex flex-col items-center justify-center gap-4 p-12
            border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-200
            ${isDragOver
              ? 'border-teal-500 bg-teal-100 scale-[1.02]'
              : 'border-teal-300 bg-teal-50 hover:border-teal-400 hover:bg-teal-100'}
          `}
        >
          <Upload className="w-12 h-12 text-teal-500" />
          <p className="text-teal-700 font-medium text-center">
            {t('invoiceIntake.upload.dropzone', 'גרור חשבונית לכאן או לחץ לבחירה')}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
        </div>
      ) : (
        <div className="bg-white border border-teal-200 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-4">
            {previewUrl ? (
              <img src={previewUrl} alt="" className="w-20 h-20 object-cover rounded-lg border" />
            ) : (
              <div className="w-20 h-20 bg-red-50 rounded-lg border flex items-center justify-center">
                <FileText className="w-8 h-8 text-red-500" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{selectedFile.name}</p>
              <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
            </div>
            <button
              onClick={clearSelection}
              disabled={isScanning}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleScan}
              disabled={isScanning}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white font-medium rounded-xl hover:bg-teal-700 disabled:opacity-70 transition-colors"
            >
              {isScanning ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{scanProgress}</span>
                </>
              ) : (
                t('invoiceIntake.upload.scan', 'סרוק חשבונית')
              )}
            </button>
            <button
              onClick={clearSelection}
              disabled={isScanning}
              className="px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              {t('invoiceIntake.upload.cancel', 'בטל')}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => cameraInputRef.current?.click()}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border border-teal-200 text-teal-700 font-medium rounded-xl hover:bg-teal-50 transition-colors"
      >
        <Camera className="w-5 h-5" />
        {t('invoiceIntake.upload.camera', 'צלם תמונה')}
      </button>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
      />

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button
            onClick={handleScan}
            className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 font-medium shrink-0"
          >
            <RefreshCw className="w-4 h-4" />
            {t('invoiceIntake.upload.retry', 'נסה שוב')}
          </button>
        </div>
      )}
    </div>
  );
}
